import { useEffect, useState } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { toast } from "sonner";

import api, { apiError, fmtNum } from "@/lib/api";

import { Card, Field, TextInput, TextArea, SelectInput, SuggestInput, CrudActions, SectionTitle } from "@/components/kit";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { Loader2 } from "lucide-react";

import { useMasters, opt, vehicleOptions, matchVehicle } from "@/lib/useMasters";

import { InwardEditDialog, OutwardEditDialog } from "@/components/TxnEditDialog";



export default function TxnRecent({ kind, title, columns, editFields }) {

  const qc = useQueryClient();

  const { data = [], isLoading } = useQuery({

    queryKey: ["txn", kind],

    queryFn: async () => (await api.get(`/txn/${kind}`)).data,

  });

  const [editing, setEditing] = useState(null);



  const remove = async (row) => {

    const num = row.inward_number || row.outward_number || row.transfer_number;

    if (!window.confirm(`Reverse and delete ${num}? Stock bags and weight will be adjusted through the lot ledger.`)) return;

    try {

      await api.delete(`/txn/${kind}/${row.id}`);

      toast.success(`${num} reversed`);

      qc.invalidateQueries();

    } catch (e) { toast.error(apiError(e)); }

  };



  const onSaved = () => {

    qc.invalidateQueries({ queryKey: ["txn", kind] });

    qc.invalidateQueries();

    setEditing(null);

  };



  return (

    <Card className="p-5 mt-6" data-testid={`${kind}-recent`}>

      <SectionTitle>{title}</SectionTitle>

      {isLoading ? (

        <div className="grid place-items-center py-8"><Loader2 className="animate-spin text-emerald-700" size={22} /></div>

      ) : (

        <div className="overflow-x-auto">

          <table className="w-full text-sm">

            <thead className="bg-stone-50 text-left text-[11px] uppercase text-stone-500">

              <tr>

                {columns.map((c) => <th key={c[0]} className="px-3 py-2">{c[1]}</th>)}

                <th className="px-3 py-2 text-right">Actions</th>

              </tr>

            </thead>

            <tbody>

              {data.length === 0 && <tr><td colSpan={columns.length + 1} className="px-3 py-6 text-center text-stone-400">No records yet</td></tr>}

              {data.map((r) => (

                <tr key={r.id} className="border-t border-stone-100 even:bg-stone-50/50">

                  {columns.map((c) => <td key={c[0]} className="px-3 py-2">{c[2] ? c[2](r) : (r[c[0]] ?? "—")}</td>)}

                  <td className="px-3 py-2">

                    <CrudActions testid={`${kind}-${r.id}`} onEdit={() => setEditing(r)} onDelete={() => remove(r)} />

                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

      )}

      {kind === "inward" && <InwardEditDialog record={editing} onClose={() => setEditing(null)} onSaved={onSaved} />}

      {kind === "outward" && <OutwardEditDialog record={editing} onClose={() => setEditing(null)} onSaved={onSaved} />}

      {kind === "transfer" && (

        <TransferEditDialog record={editing} fields={editFields} onClose={() => setEditing(null)} onSaved={onSaved} kind={kind} />

      )}

    </Card>

  );

}



function TransferEditDialog({ kind, record, fields, onClose, onSaved }) {

  const m = useMasters();

  const [f, setF] = useState({});

  const [saving, setSaving] = useState(false);



  useEffect(() => {

    if (!record) return;

    const next = {};

    fields.forEach(([k]) => {

      let v = record[k];

      if (k === "date" && v) v = String(v).slice(0, 10);

      next[k] = v ?? "";

    });

    setF(next);

  }, [record, fields]);



  const save = async () => {

    setSaving(true);

    try {

      const body = { ...f };

      fields.forEach(([k, , type]) => {

        if (type === "party" && body[k] !== "" && body[k] != null) body[k] = Number(body[k]);

        if (type === "party" && body[k] === "") body[k] = null;

        if (type === "vehicle") {

          const v = matchVehicle(m.vehicles, body[k], body.vehicle_number);

          body[k] = v ? v.id : null;

        }

      });

      await api.put(`/txn/${kind}/${record.id}`, body);

      toast.success("Updated");

      onSaved();

    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }

  };



  return (

    <Dialog open={!!record} onOpenChange={(o) => !o && onClose()}>

      <DialogContent data-testid={`${kind}-edit-dialog`}>

        <DialogHeader><DialogTitle className="font-display">Edit transfer</DialogTitle></DialogHeader>

        <div className="grid grid-cols-2 gap-3">

          {fields.map(([k, label, type]) => (

            <Field key={k} label={label} className={type === "textarea" ? "col-span-2" : ""}>

              {type === "date" && <TextInput type="date" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} />}

              {type === "textarea" && <TextArea rows={2} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} />}

            </Field>

          ))}

        </div>

        <button onClick={save} disabled={saving} className="mt-2 flex h-11 items-center justify-center gap-2 rounded-lg bg-emerald-800 font-semibold text-white hover:bg-emerald-900 disabled:opacity-60">

          {saving ? <Loader2 size={18} className="animate-spin" /> : null} Update

        </button>

      </DialogContent>

    </Dialog>

  );

}



export const inwardColumns = [

  ["inward_number", "Number"],

  ["date", "Date"],

  ["warehouse_name", "Warehouse"],

  ["item_name", "Item"],

  ["stock_owner_name", "Owner"],

  ["bags", "Bags", (r) => fmtNum(r.bags)],

];

export const inwardFields = [];



export const outwardColumns = [

  ["outward_number", "Number"],

  ["date", "Date"],

  ["lot_number", "Lot"],

  ["item_name", "Item"],

  ["receiver_name", "Receiver"],

  ["bags", "Dispatched", (r) => fmtNum(r.bags)],

];

export const outwardFields = [];



export const transferColumns = [

  ["transfer_number", "Number"],

  ["date", "Date"],

  ["source_lot_number", "From"],

  ["dest_lot_number", "To"],

  ["bags", "Bags", (r) => fmtNum(r.bags)],

];

export const transferFields = [

  ["date", "Date", "date"],

  ["reason", "Reason", "textarea"],

];


